/* eslint-disable no-console */
import { IUser, MfaMethod } from '@dealpoint/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { batch } from 'react-redux'
import useSWR from 'swr'

import { APIClient } from '../APIClient'
import { backendLogin, backendSignOut, getSessionUser } from '../auth/backendAuth'
import { groups as permissionGroups } from '../permissions'
import { AdminPermission } from '../types'

import useOrgInfo from './useOrgInfo'

export type MfaType = 'SMS_MFA' | 'SOFTWARE_TOKEN_MFA'

const NOT_SUPPORTED = 'This feature is not available in the current build.'

/**
 * Backend-backed replacement for the original Cognito/Amplify useUser. Preserves
 * the exact return interface so consuming components are unchanged. Auth now
 * flows through aristotle-backend (see auth/backendAuth). MFA/TOTP/Cognito-only
 * features are stubbed until backend equivalents are wired.
 */
export default function useUser() {
  const { orgInfo } = useOrgInfo()
  const [cognitoUser, setCognitoUser] = useState(_cognitoUser)
  // The legacy key was `/users/details/${username}`; aristotle-backend exposes
  // the current user at `/user/me`.
  const { data, error, loading, mutate } = useSWR(cognitoUser && `/user/me`)

  useEffect(() => {
    let mounted = true
    if (!cognitoUser) {
      _promise = _promise ?? getSessionUser()
      // Guard against setting state after unmount (avoids the React
      // "state update on an unmounted component" leak warning).
      _promise.then((u) => mounted && setCognitoUser(u)).catch(console.error)
    }
    return () => {
      mounted = false
    }
  }, [cognitoUser, setCognitoUser])

  const groups = useMemo(() => {
    const idTokenPayload = cognitoUser?.signInUserSession?.idToken?.payload ?? {}
    return idTokenPayload ? idTokenPayload['cognito:groups'] : ['user']
  }, [cognitoUser?.signInUserSession?.idToken?.payload])

  const user = useMemo<IUser>(
    () => ({ ...data, phoneVerified: cognitoUser?.attributes?.phone_number_verified }),
    [cognitoUser?.attributes?.phone_number_verified, data]
  )

  const changePassword = useCallback(
    (oldPassword: string, newPassword: string) =>
      APIClient.makeAPICall('POST', '/auth/change-password', {
        body: { oldPassword, newPassword },
      }),
    []
  )

  const completeNewPassword = useCallback(async (_password: string) => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  const confirmSignIn = useCallback(async (_code: string, _challengeName: MfaType) => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  const forgotPassword = useCallback(
    (email: string) =>
      APIClient.makeAPICall('POST', '/auth/forget-password', {
        body: { email },
        noAuthorizationHeader: true,
      }),
    []
  )

  const forgotPasswordSubmit = useCallback(
    async (email: string, password: string, code: string) => {
      await APIClient.makeAPICall('POST', '/auth/reset-password', {
        body: { email, password, code },
        noAuthorizationHeader: true,
      })
    },
    []
  )

  const getTotpCode = useCallback(async () => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  const hasPermission = useCallback(
    (permissions: AdminPermission[]) => {
      if (!groups) {
        return null
      }

      const userPermissions = (groups ?? []).reduce(
        (acc, groupName) => acc.concat(permissionGroups[groupName] ?? []),
        []
      )

      return permissions.every((p) => userPermissions.includes(p))
    },
    [groups]
  )

  const isSignedIn = useCallback(async () => {
    if (_cognitoUser) {
      return true
    }

    if (_promise) {
      return _promise.then(Boolean).catch(() => false)
    }

    return false
  }, [])

  const update = useCallback(
    async (user: Partial<IUser>) =>
      mutate(() => APIClient.makeAPICall('PUT', `/user/me`, { body: user }), {
        optimisticData: (original) => ({ ...original, ...user }),
      }),
    [mutate]
  )

  const setMfaMethod = useCallback(
    async (method: MfaMethod) => {
      if (method !== user.twoFactorMethod) {
        await update({ twoFactorMethod: method })
      }
    },
    [update, user.twoFactorMethod]
  )

  const sendSmsVerificationCode = useCallback(async () => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  const signin = useCallback(async (email: string, password: string) => {
    const sessionUser = await signinSessionUser(email, password)
    setCognitoUser(sessionUser)
    return sessionUser
  }, [])

  const resendSignInCode = useCallback(async (_password: string) => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  const signout = useCallback(async () => {
    await signoutSessionUser()
    batch(() => {
      setCognitoUser(undefined)
      mutate(undefined, { revalidate: false })
    })
  }, [mutate])

  const verifyPhone = useCallback(async (_code: string) => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  const verifyTotp = useCallback(async (_token: string) => {
    throw new Error(NOT_SUPPORTED)
  }, [])

  return {
    cognitoUser,
    error,
    groups,
    loading: loading || !cognitoUser,
    user,
    changePassword,
    completeNewPassword,
    confirmSignIn,
    forgotPassword,
    forgotPasswordSubmit,
    getTotpCode,
    hasPermission,
    isSignedIn,
    resendSignInCode,
    sendSmsVerificationCode,
    setMfaMethod,
    signin,
    signout,
    update,
    verifyPhone,
    verifyTotp,
  }
}

let _promise: Promise<any>
let _cognitoUser: any

const signinSessionUser = async (email: string, password: string) => {
  _promise = backendLogin(email, password)
    .then((result) => {
      // The legacy useUser().signin path has no 2FA challenge UI; the active
      // login (AuthGate) handles requires2FA via backendVerify2FA directly.
      if (result.status === 'requires2FA') {
        throw new Error('Two-factor authentication is required. Please sign in again.')
      }
      return result.user
    })
    .catch((error) => {
      console.error('Authentication Error', error)
      throw error
    })
  return (_cognitoUser = await _promise)
}

const signoutSessionUser = async () => {
  backendSignOut()
  _promise = null
  _cognitoUser = null
}
